import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, TrendingUp, RefreshCw, Lock, Search, Layers, Rocket, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const sections = [
  {
    id: "usecases",
    title: "What Clients Hire Us For",
    content: (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {[
          { icon: Shield, title: "Stabilize production", desc: "Reduce incidents, improve monitoring, tighten processes.", color: "#30D158" },
          { icon: TrendingUp, title: "Scale compute", desc: "HPC/AI workload reliability and performance tuning.", color: "#2997FF" },
          { icon: RefreshCw, title: "Modernize platforms", desc: "Cloud migration, refactors, and automation.", color: "#BF5AF2" },
          { icon: Lock, title: "Enable AI safely", desc: "Governance, secure deployment, measurable outcomes.", color: "#FF9F0A" }
        ].map((item, index) => {
          const Icon = item.icon;
          return (
            <div key={index} className="service-card p-6 text-center">
              <div
                className="w-12 h-12 mx-auto rounded-2xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${item.color}20` }}
              >
                <Icon size={24} style={{ color: item.color }} />
              </div>
              <h3 className="text-lg font-semibold text-[#F5F5F7] mb-2">{item.title}</h3>
              <p className="text-sm text-[#86868B]">{item.desc}</p>
            </div>
          );
        })}
      </div>
    )
  },
  {
    id: "process",
    title: "How We Work",
    content: (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
        {[
          { num: "1", icon: Search, title: "Discovery", desc: "Goals, constraints, risks, and success metrics." },
          { num: "2", icon: Layers, title: "Design", desc: "Architecture, delivery plan, and operational model." },
          { num: "3", icon: Rocket, title: "Build + Run", desc: "Implementation, documentation, and support handover." }
        ].map((step, index) => {
          const Icon = step.icon;
          return (
            <div key={index} className="text-center">
              <div className="relative inline-flex items-center justify-center w-16 h-16 mb-4">
                <div className="absolute inset-0 rounded-full bg-[#2997FF]/10" />
                <div className="absolute inset-1 rounded-full bg-[#2997FF]/20 flex items-center justify-center">
                  <span className="text-xl font-bold text-[#2997FF]">{step.num}</span>
                </div>
              </div>
              <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-[#2C2C2E] flex items-center justify-center">
                <Icon size={20} className="text-[#F5F5F7]" />
              </div>
              <h3 className="text-lg font-semibold text-[#F5F5F7] mb-2">{step.title}</h3>
              <p className="text-sm text-[#86868B]">{step.desc}</p>
            </div>
          );
        })}
      </div>
    )
  },
  {
    id: "delivery",
    title: "Delivery Model",
    content: (
      <div className="flex justify-center">
        <div className="flex flex-col gap-4 text-left">
          {[
            "Pragmatic approach that keeps things moving fast",
            "Reliability without sacrificing speed",
            "Clean documentation at every stage",
            "Seamless handover to your team"
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-3 text-lg text-[#86868B]">
              <div className="w-1.5 h-1.5 rounded-full bg-[#2997FF] flex-shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    )
  },
  {
    id: "proposal",
    title: "Fixed-Scope Proposals",
    content: (
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-lg text-[#86868B] leading-relaxed mb-8">
          Need a fixed-scope proposal? Share your goals and constraints with us. 
          We'll respond with a detailed plan including timelines, deliverables, 
          and pricing — no surprises, just clear expectations.
        </p>
        <Link
          to="/contact"
          className="btn-primary px-8 py-4 rounded-full text-base font-medium inline-flex items-center gap-2"
        >
          Get a Proposal
          <ArrowRight size={18} />
        </Link>
      </div>
    )
  }
];

const SolutionsPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? sections.length - 1 : prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === sections.length - 1 ? 0 : prev + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div data-testid="solutions-page" className="pt-20">
      {/* Main Content */}
      <section className="py-16 md:py-24 bg-black min-h-[100dvh] flex items-center">
        <div className="max-w-[1200px] mx-auto px-6 w-full">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center gap-8">
            {/* Left Arrow */}
            <button
              onClick={goToPrevious}
              className="flex-shrink-0 w-14 h-14 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] hover:text-[#F5F5F7] hover:border-[#2997FF]/50 transition-all"
              data-testid="solutions-prev"
              aria-label="Previous section"
            >
              <ChevronLeft size={28} />
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="text-center"
                >
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#F5F5F7] tracking-tight mb-10" data-testid="solutions-title">
                    {sections[currentIndex].title}
                  </h1>
                  <div className="max-w-4xl mx-auto">
                    {sections[currentIndex].content}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots Indicator */}
              <div className="flex items-center justify-center gap-2 mt-12">
                {sections.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex ? "bg-[#2997FF] w-6" : "bg-[#86868B]/50 hover:bg-[#86868B]"
                    }`}
                    aria-label={`Go to section ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Right Arrow */}
            <button
              onClick={goToNext}
              className="flex-shrink-0 w-14 h-14 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] hover:text-[#F5F5F7] hover:border-[#2997FF]/50 transition-all"
              data-testid="solutions-next"
              aria-label="Next section"
            >
              <ChevronRight size={28} />
            </button>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden">
            {/* Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="text-center min-h-[350px]"
              >
                <h1 className="text-2xl font-semibold text-[#F5F5F7] tracking-tight mb-8">
                  {sections[currentIndex].title}
                </h1>
                <div className="max-w-full">
                  {sections[currentIndex].content}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-6 mt-10">
              <button
                onClick={goToPrevious}
                className="w-12 h-12 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] active:text-[#F5F5F7] active:border-[#2997FF]/50 transition-all"
                aria-label="Previous section"
              >
                <ChevronLeft size={24} />
              </button>

              {/* Dots */}
              <div className="flex items-center gap-2">
                {sections.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex ? "bg-[#2997FF] w-4" : "bg-[#86868B]/50"
                    }`}
                    aria-label={`Go to section ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={goToNext}
                className="w-12 h-12 rounded-full bg-[#1C1C1E] border border-white/10 flex items-center justify-center text-[#86868B] active:text-[#F5F5F7] active:border-[#2997FF]/50 transition-all"
                aria-label="Next section"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage;
